import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { TooltipLayerHandle, TooltipLayerState } from './index.types';

// This component renders the tooltip markup in a fixed-position portal and exposes
// an imperative API so the parent can show/hide it without re‑rendering the filter list.
export const OSearchResultsTooltipLayer = forwardRef<TooltipLayerHandle>((_, ref) => {
  const tooltipDialogRef = useRef<HTMLDivElement | null>(null);
  const measurementRafRef = useRef<number | null>(null);
  const [tooltipState, setTooltipState] = useState<TooltipLayerState>({
    visible: false,
    offsetLeft: 0,
    offsetTop: 0,
    tooltipDialogElemOffsetHeight: 0,
    tooltipDialogElemOffsetWidth: 0,
    anchorOffset: 0,
    tooltipModalPosition: 'top',
    headingText: '',
    bodyText: '',
  });

  const clearPendingMeasurement = () => {
    if (measurementRafRef.current) {
      cancelAnimationFrame(measurementRafRef.current);
      measurementRafRef.current = null;
    }
  };

  useImperativeHandle(ref, () => ({
    showTooltip({ heading = '', text = '', anchor }) {
      if (!anchor) {
        return;
      }
      clearPendingMeasurement();
      // Push the latest content into state so the DOM reflects it before we measure.
      setTooltipState(prev => ({
        ...prev,
        headingText: heading,
        bodyText: text,
      }));
      const measure = () => {
        const tooltipElem = tooltipDialogRef.current;
        if (!tooltipElem) {
          return;
        }
        const modalRect = tooltipElem.getBoundingClientRect();
        const iconRect = anchor.getBoundingClientRect();
        let offsetLeftValue = iconRect.left + window.scrollX;
        const offsetTopValue = iconRect.top + window.scrollY;
        const rightOverflow = offsetLeftValue + modalRect.width / 2 - window.innerWidth;

        if (rightOverflow > 0) {
          offsetLeftValue -= rightOverflow;
        } else if (offsetLeftValue - modalRect.width / 2 - 2 < 0) {
          offsetLeftValue = modalRect.width / 2 + 4;
        }

        const iconCenterX = iconRect.left + iconRect.width / 2 + window.scrollX;
        const modalLeft = offsetLeftValue;
        const modalWidth = modalRect.width;
        const anchorOffset = iconCenterX - modalLeft + modalWidth / 2;
        const tooltipModalPosition = offsetTopValue - modalRect.height < 0 ? 'bottom' : 'top';

        setTooltipState(prev => ({
          ...prev,
          visible: true,
          offsetLeft: offsetLeftValue,
          offsetTop: offsetTopValue,
          tooltipDialogElemOffsetHeight: modalRect.height,
          tooltipDialogElemOffsetWidth: modalRect.width,
          anchorOffset,
          tooltipModalPosition,
        }));
        measurementRafRef.current = null;
      };

      // Wait two frames: first gives React a chance to flush content state, second measures with the new layout.
      measurementRafRef.current = requestAnimationFrame(() => {
        measurementRafRef.current = requestAnimationFrame(measure);
      });
    },
    hideTooltip() {
      clearPendingMeasurement();
      setTooltipState(prev => ({ ...prev, visible: false }));
    },
  }));

  useEffect(() => {
    return () => {
      clearPendingMeasurement();
    };
  }, []);

  const {
    visible,
    offsetLeft,
    offsetTop,
    tooltipDialogElemOffsetHeight,
    tooltipDialogElemOffsetWidth,
    anchorOffset,
    tooltipModalPosition,
    headingText,
    bodyText,
  } = tooltipState;

  const top = tooltipModalPosition === 'top' ? offsetTop - tooltipDialogElemOffsetHeight - 16 : offsetTop + 32;
  const left = offsetLeft - tooltipDialogElemOffsetWidth / 2 - 2;

  const tooltipStyle: React.CSSProperties = {
    visibility: visible ? 'visible' : 'hidden',
    top,
    left,
    transform: 'translateX(0)',
  };
  (tooltipStyle as Record<string, string | number>)['--tooltip-anchor-offset'] = `${anchorOffset}px`;

  return (
    <>
      {/* Screen reader: aria-live announces only the tooltip content; avoids focus trap reading "button All Filters" */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {visible ? `${headingText} ${bodyText}` : ''}
      </div>
      <div
        className="tooltip-dialog"
        ref={tooltipDialogRef}
        role="tooltip"
        id="tooltip-dialog"
        style={tooltipStyle}
        aria-labelledby="tooltip-dialog-heading"
        aria-describedby="tooltip-dialog-body"
      >
        <div className={`m-popover-${tooltipModalPosition} inverse`} data-content="Inverse Theme">
          <div className="m-popover-header">
            <h2 id="tooltip-dialog-heading" className="t-subtitle-l mb-0">
              {headingText}
            </h2>
          </div>
          <p id="tooltip-dialog-body" className="t-font-s mb-0">
            {bodyText}
          </p>
        </div>
      </div>
    </>
  );
});
